/**
 * Run 12 item G (DECIDED 20261012, Barna): every email that asks staff to act
 * links straight to the action.
 *
 * Two links, near the top and before the order details:
 *   * *Megnyitás a konzolban*: the order's own page, or the view the action
 *     has of its own (the sign-off screen, the queue, the refunds list);
 *   * the console's home page, as a fallback.
 *
 * **Built from the environment, never hard-coded.** The base is
 * `payload.site.console_url`, which the worker sets from its own
 * `PUBLIC_SITE_URL` (test links go to the test console, production links to
 * production). With no base there are no links, rather than a guessed address.
 *
 * **The links grant nothing.** A path and, at most, the order's ID: no token,
 * no reference, no customer data. The console still asks the person to sign
 * in, and the server still decides what they may see.
 *
 * **Never in a customer email.** `staffLinks` returns nothing for the
 * customer audience, whatever the payload carries, and the worker never gives
 * a customer payload a console base in the first place.
 */

import type { Block } from './layout.ts'
import type { EmailPayload } from './payload.ts'

export const OPEN_IN_CONSOLE = 'Megnyitás a konzolban'
export const CONSOLE_HOME = 'A konzol kezdőlapja'

/** Where an email's action lives, relative to `/staff`. `order` is the order's
 *  own page, with an optional section; a string is a view of its own; null
 *  means the action has no page beyond the home page. */
export type StaffTarget =
  | { kind: 'order'; section?: string }
  | { kind: 'view'; path: string }
  | null

/**
 * One row per staff, finance, kitchen and admin email. The action is what the
 * email asks the recipient to do; the target is the page they do it on. A
 * view is chosen only where the recipient's role can open it (§11.7's
 * `console_views`): C12 goes to catering, who cannot open the alerts view, so
 * it links to the order.
 */
export const STAFF_ACTIONS: Record<string, { action: string; target: (payload: EmailPayload) => StaffTarget }> = {
  C1: { action: 'Nincs teendő: tájékoztatás a visszaigazolásról.', target: () => ({ kind: 'order' }) },
  C2: {
    action: 'Visszaigazolás vagy felülvizsgálat: jóváhagyás, javaslat vagy elutasítás.',
    target: (payload) =>
      (payload.internal?.confirmation_reasons ?? []).length > 0
        ? { kind: 'view', path: 'confirmations' }
        : { kind: 'view', path: 'review' },
  },
  C3: { action: 'Felülvizsgálat (emlékeztető).', target: () => ({ kind: 'view', path: 'review' }) },
  C4: { action: 'Felülvizsgálat (eszkaláció).', target: () => ({ kind: 'view', path: 'review' }) },
  C5: { action: 'Az érdeklődő visszahívása.', target: () => null },
  C7: { action: 'A lejárt fizetési határidő ellenőrzése.', target: () => ({ kind: 'order', section: 'penzmozgas' }) },
  C10: { action: 'A napi véglegesítés áttekintése.', target: () => ({ kind: 'view', path: 'day' }) },
  C11: { action: 'A napi kapacitás ellenőrzése.', target: () => ({ kind: 'view', path: 'day' }) },
  C12: { action: 'A katalógusváltozás kezelése a rendelésen.', target: () => ({ kind: 'order' }) },
  C13: { action: 'Nincs teendő: a módosítás érvénybe lépett.', target: () => ({ kind: 'order' }) },
  C14: { action: 'Az átadás rögzítése.', target: () => ({ kind: 'view', path: 'handover' }) },
  C15: { action: 'A lezárás kitöltése.', target: () => ({ kind: 'view', path: 'handover' }) },
  C16: { action: 'A megválaszolatlan javaslat kezelése.', target: () => ({ kind: 'order', section: 'modositasok' }) },
  C17: { action: 'A vendég visszahívása.', target: () => ({ kind: 'view', path: 'callbacks' }) },
  A1: {
    action: 'A sikertelen levél kezelése.',
    target: (payload) => (payload.order_id ? { kind: 'order', section: 'levelek' } : null),
  },
  /* A2 has two occasions since A4 of the catalogue brief of 19 October 2026:
     the roles sheet (§11.12.4) and the catalogue Sheet. The digest says which,
     and the link goes where the person would actually look. */
  A2: {
    action: 'A szinkron elutasított változásának ellenőrzése.',
    target: (payload) =>
      (payload.digest as Record<string, unknown> | null)?.kind === 'catalogue'
        ? { kind: 'view', path: 'catalogue' }
        : { kind: 'view', path: 'settings' },
  },
  /* A3 (DECIDED 20261019): the allergen guard withdrew a dish. */
  A3: {
    action: 'A visszavont fogás allergénadatának ellenőrzése és újrahitelesítése.',
    target: () => ({ kind: 'view', path: 'catalogue' }),
  },
  /* A4 (SimplePay test, 20261103): an IPN status a one-step charge does not expect. */
  A4: {
    action: 'A tranzakció ellenőrzése a SimplePay kereskedői felületén.',
    target: () => ({ kind: 'order', section: 'penzmozgas' }),
  },
  F1: { action: 'A beérkezett utalás ellenőrzése és rögzítése.', target: () => ({ kind: 'order', section: 'penzmozgas' }) },
  F2: { action: 'A sikertelen kártyás fizetés kezelése.', target: () => ({ kind: 'order', section: 'penzmozgas' }) },
  F4: { action: 'A visszatérítés vagy mozgatás rögzítése.', target: () => ({ kind: 'view', path: 'refunds' }) },
  F5: { action: 'A sikertelen dokumentum kezelése.', target: () => ({ kind: 'order', section: 'szamlak' }) },
  F6: { action: 'Az eltérő utalt összeg kezelése.', target: () => ({ kind: 'order', section: 'penzmozgas' }) },
  F7: { action: 'A lejárt visszatérítés rögzítése.', target: () => ({ kind: 'view', path: 'refunds' }) },
  F8: { action: 'A visszatartott végszámla kiállítása.', target: () => ({ kind: 'order', section: 'szamlak' }) },
  F9: { action: 'A PDF nélküli dokumentum kézi kiküldése.', target: () => ({ kind: 'order', section: 'szamlak' }) },
  F10: { action: 'A szamlazz.hu előlegszámla ellenőrzése.', target: () => ({ kind: 'order', section: 'szamlak' }) },
  K1: { action: 'Gyártás a rendelés szerint.', target: () => ({ kind: 'order' }) },
  K2: { action: 'Gyártás a rendelés szerint (rövid határidő).', target: () => ({ kind: 'order' }) },
  K3: { action: 'A módosított gyártás.', target: () => ({ kind: 'order' }) },
  K4: { action: 'A törölt gyártás leállítása.', target: () => ({ kind: 'order' }) },
  K5: { action: 'A napi gyártás.', target: () => ({ kind: 'view', path: 'day' }) },
  K6: { action: 'Csomagolás és címkék.', target: () => ({ kind: 'view', path: 'day' }) },
  K7: { action: 'A heti előrejelzés áttekintése.', target: () => ({ kind: 'view', path: 'day' }) },
  K8: { action: 'Az étrendi megjegyzés aláírása.', target: () => ({ kind: 'view', path: 'dietary' }) },
  K9: { action: 'A katalógusváltozás ellenőrzése a gyártásban.', target: () => ({ kind: 'order' }) },
}

/** An order ID is a UUID; anything else is not put in a link. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The two addresses, or null where the email gets no links. */
export function staffLinkTargets(payload: EmailPayload): { action: string | null; home: string } | null {
  if (payload.audience === 'customer') return null
  const base = (payload.site.console_url ?? '').trim().replace(/\/+$/, '')
  if (!/^https?:\/\//.test(base)) return null
  const entry = STAFF_ACTIONS[payload.code]
  if (entry === undefined) return null
  const target = entry.target(payload)
  const home = `${base}/`
  if (target === null) return { action: null, home }
  if (target.kind === 'view') return { action: `${base}/${target.path}`, home }
  const id = payload.order_id ?? ''
  if (!UUID.test(id)) return { action: null, home }
  return { action: `${base}/orders/${id}${target.section ? `#${target.section}` : ''}`, home }
}

/** The blocks: the action as a button, the home page as a plain link. Where
 *  the action has no page of its own (C5: the console has no enquiry view),
 *  the home page is the one link, under its own name. */
export function staffLinks(payload: EmailPayload): Block[] {
  const targets = staffLinkTargets(payload)
  if (targets === null) return []
  if (targets.action === null) return [{ kind: 'button', label: CONSOLE_HOME, href: targets.home }]
  return [
    { kind: 'button', label: OPEN_IN_CONSOLE, href: targets.action },
    { kind: 'link', label: CONSOLE_HOME, href: targets.home },
  ]
}

/** Puts the links near the top: after a leading band, before everything else. */
export function withStaffLinks(payload: EmailPayload, blocks: Block[]): Block[] {
  const links = staffLinks(payload)
  if (links.length === 0) return blocks
  const at = blocks[0]?.kind === 'band' ? 1 : 0
  return [...blocks.slice(0, at), ...links, ...blocks.slice(at)]
}
